import React from 'react'
import { PiBuildings, PiChecks } from 'react-icons/pi'
import { Stat, Separator, HelpPopover, type BadgeColor } from '@firstbite/ui'
import { cn } from '../UI/cn'
import type { ActionSummaryStructuredData } from '../../services/recommendations_api'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import type { RecommendationAgent } from '../../models/companies'
import { URGENCY_ICONS, URGENCY_TOOLTIPS } from './constants'
import { resolveAgentIcon } from './agentIcons'

type ViewMode = 'priority' | 'signal' | 'company'

// Urgency → fb-ui `Stat` chip color. Mirrors the DS `PRIORITY_META` mapping in
// the fb-ui ActionCenter block (hot→error, today→warning, this_week→blue) so this
// toolbar reads the same as the block Phase 2 will adopt.
const URGENCY_COLOR: Record<EnrichedAction['urgency'], BadgeColor> = {
  do_now: 'error',
  today: 'warning',
  this_week: 'blue',
}

interface SummaryStatsHeaderProps {
  data: ActionSummaryStructuredData
  className?: string
  visibleActions?: EnrichedAction[]
  viewMode?: ViewMode
  /** Number of companies with active actions (used in company view). */
  companiesCount?: number
  /**
   * User-facing agents from the backend AGENT_REGISTRY (delivered via
   * company-details). Drives the signal-view chips so adding a new agent
   * server-side automatically adds a chip — no FE update needed.
   */
  recommendationAgents?: RecommendationAgent[]
}

/** One summary chip's data, rendered as an inline fb-ui `Stat`. */
interface ChipData {
  key: string
  icon: React.ReactNode
  color: BadgeColor
  value: number | string
  label: string
  /** Help-popover body explaining the chip. */
  tooltip: string
}

/**
 * Summary stats header — a row of inline fb-ui `Stat` chips whose content adapts
 * to the active view mode, separated by vertical rules.
 *
 * - Priority view: Do Now / Today / This Week counts (urgency bands)
 * - Signal/Type view: One chip per enabled agent — agents come from the
 *   backend AGENT_REGISTRY via company-details, so adding a new agent
 *   server-side automatically adds its chip with no FE change.
 * - Company view: Companies / Active Actions counts
 */
export const SummaryStatsHeader: React.FC<SummaryStatsHeaderProps> = ({
  data,
  className,
  visibleActions,
  viewMode = 'priority',
  companiesCount = 0,
  recommendationAgents = [],
}) => {
  const actions = visibleActions ?? data.enriched_actions ?? []

  // Urgency band counts
  const doNowCount = actions.filter((a) => a.urgency === 'do_now').length
  const todayCount = actions.filter((a) => a.urgency === 'today').length
  const thisWeekCount = actions.filter((a) => a.urgency === 'this_week').length

  const totalActive = actions.length

  const DoNowIcon = URGENCY_ICONS.do_now
  const TodayIcon = URGENCY_ICONS.today
  const ThisWeekIcon = URGENCY_ICONS.this_week

  const agentChips: ChipData[] = recommendationAgents
    .filter((agent) => agent.enabled)
    .map((agent) => {
      const Icon = resolveAgentIcon(agent.icon)
      const memberSet = new Set(agent.signal_types)
      const count = actions.filter((a) => memberSet.has(a.signal_type)).length
      return {
        key: agent.label,
        icon: <Icon aria-hidden="true" />,
        color: 'primary' as BadgeColor,
        value: count,
        label: agent.label,
        tooltip: agent.description,
      }
    })

  const chips: ChipData[] =
    viewMode === 'priority'
      ? [
          {
            key: 'do_now',
            icon: <DoNowIcon aria-hidden="true" />,
            color: URGENCY_COLOR.do_now,
            value: doNowCount,
            label: 'Hot',
            tooltip: URGENCY_TOOLTIPS.do_now.content,
          },
          {
            key: 'today',
            icon: <TodayIcon aria-hidden="true" />,
            color: URGENCY_COLOR.today,
            value: todayCount,
            label: 'Today',
            tooltip: URGENCY_TOOLTIPS.today.content,
          },
          {
            key: 'this_week',
            icon: <ThisWeekIcon aria-hidden="true" />,
            color: URGENCY_COLOR.this_week,
            value: thisWeekCount,
            label: 'This Week',
            tooltip: URGENCY_TOOLTIPS.this_week.content,
          },
        ]
      : viewMode === 'signal'
        ? agentChips
        : [
            {
              key: 'companies',
              icon: <PiBuildings aria-hidden="true" />,
              color: 'primary',
              value: companiesCount,
              label: 'Companies',
              tooltip:
                'Number of companies with at least one active recommended action.',
            },
            {
              key: 'total',
              icon: <PiChecks aria-hidden="true" />,
              color: 'primary',
              value: totalActive,
              label: 'Total',
              tooltip: `${totalActive} active action${totalActive === 1 ? '' : 's'} across ${companiesCount} ${companiesCount === 1 ? 'company' : 'companies'}.`,
            },
          ]

  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}
    >
      {chips.map((chip, i) => (
        <React.Fragment key={chip.key}>
          {i > 0 && (
            <Separator orientation="vertical" decorative className="h-7" />
          )}
          <Stat
            density="inline"
            size="md"
            color={chip.color}
            icon={chip.icon}
            value={chip.value}
            label={chip.label}
            help={
              <HelpPopover triggerSize="sm" size="md">
                {chip.tooltip}
              </HelpPopover>
            }
          />
        </React.Fragment>
      ))}
    </div>
  )
}
